import { useState, useEffect } from 'react';
import LocationSelect from '../components/shared/LocationSelect.jsx';
import { apiFetch, useApi } from '../hooks/useApi.js';
import PrintButton from '../components/shared/PrintButton.jsx';
import { ConsignmentList, ConsignmentDetail, AddToConsignmentModal, openPdf } from './Consignments.jsx';
import { uiConfirm, uiAlert } from '../utils/dialogs.js';

// ── Primitives ────────────────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'opacity .15s', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1 };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' } };
  return <button {...props} style={{ ...base, ...(v[variant] || v.primary), ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 520, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted)', lineHeight: 1 }}>&#215;</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}><label style={{ fontSize: 13, fontWeight: 500 }}>{label}</label>{children}</div>; }
function Input(props) { return <input {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />; }
function Select({ children, ...props }) { return <select {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' }}>{children}</select>; }
function Grid2({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>; }
function BtnRow({ children }) { return <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>{children}</div>; }
function Err({ msg }) { return msg ? <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{msg}</p> : null; }

function fmt(d) { return d ? new Date(d).toLocaleDateString('en-GB') : '—'; }

const TYPE_C = { pallet: '#3b82f6', ibc: '#8b5cf6', cask: '#f59e0b', case: '#10b981' };
function TypeBadge({ type }) {
  const c = TYPE_C[type] || '#6b7280';
  return <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: c + '22', color: c }}>{type}</span>;
}

const DUTY_C = { in_bond: { bg: '#dbeafe', color: '#1e40af' }, duty_paid: { bg: '#dcfce7', color: '#166534' }, duty_free: { bg: '#ede9fe', color: '#5b21b6' }, mixed: { bg: '#fef3c7', color: '#92400e' }, not_applicable: { bg: '#f3f4f6', color: '#6b7280' } };
const DUTY_LABEL = { in_bond: 'in bond', duty_paid: 'duty paid', duty_free: 'duty free', mixed: 'mixed — bond & duty paid', not_applicable: 'no duty' };
// A unit's duty status is that of the stock on it — Blake works it out; it
// isn't set by hand.
function DutyBadge({ status }) {
  const c = DUTY_C[status] || { bg: '#f3f4f6', color: '#6b7280' };
  return <span title="Worked out from the stock on this unit" style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>{DUTY_LABEL[status] || status?.replace(/_/g,' ')}</span>;
}

// ── Create unit modal ─────────────────────────────────────────────────────────
function CreateUnitModal({ onClose, onSaved }) {
  const { data: locData } = useApi('/warehouse/locations');
  const locations = locData?.locations ?? locData ?? [];
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData ?? [];

  const [form, setForm] = useState({ name: '', unit_type: 'pallet', location_id: '', owner_type: 'PLD', owner_client_id: '', gross_weight_kg: '', net_weight_kg: '' });
  // What's going on it now — moved from where it is (Joe, 15 Oct 2026).
  const [putItem, setPutItem] = useState(null); const [putQuery, setPutQuery] = useState('');
  const [putQty, setPutQty] = useState(''); const [putSrc, setPutSrc] = useState('');
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch('/storage-units', {
        method: 'POST',
        body: {
          ...form, gross_weight_kg: form.gross_weight_kg ? parseFloat(form.gross_weight_kg) : undefined, net_weight_kg: form.net_weight_kg ? parseFloat(form.net_weight_kg) : undefined,
          location_id: form.location_id || undefined, owner_client_id: form.owner_client_id || undefined,
          put: putItem && Number(putQty) > 0 && putSrc ? [{ stock_item_id: putItem.id, quantity: parseFloat(putQty), from_location_id: putSrc.split('|')[0], duty_status: putSrc.split('|')[1] }] : [],
        },
      });
      onSaved();
    } catch(e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Create Storage Unit" onClose={onClose}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Field label="Name"><Input value={form.name} onChange={set('name')} placeholder="e.g. Cimbroni October export" autoFocus /></Field>
        <Field label="Unit type *"><Select value={form.unit_type} onChange={set('unit_type')} required>
          {['pallet','ibc','cask','case'].map(t => <option key={t} value={t}>{t}</option>)}
        </Select></Field>
        <Field label="Location"><LocationSelect locations={locations} value={form.location_id} onChange={set('location_id')} placeholder="No location assigned" /></Field>
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>What's going on it <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional — more can go on later)</span></div>
          <Field label="Stock item"><ContentLineSearch query={putQuery} hasSelection={!!putItem} onQueryChange={v => { setPutQuery(v); setPutItem(null); }} onSelect={it => { setPutItem(it); setPutQuery(it.name); }} /></Field>
          {putItem && <Field label={`How much (${putItem.unit || ''})`}><Input type="number" min="0" step="any" value={putQty} onChange={e => setPutQty(e.target.value)} /></Field>}
          {putItem && <Field label="Where it is now"><StockSourcePicker itemId={putItem.id} unitLocationId={form.location_id} quantity={putQty} value={putSrc} onChange={setPutSrc} unit={putItem.unit} /></Field>}
          {putItem && putSrc && form.location_id && putSrc.split('|')[0] !== form.location_id && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>It's moved from there to the unit's location.</p>}
          {putItem && putSrc && !form.location_id && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>No location chosen above — the unit is taken to be where the stock is.</p>}
        </div>
        <Grid2>
          <Field label="Owner"><Select value={form.owner_type} onChange={set('owner_type')}><option value="PLD">PLD</option><option value="CONTRACT">Client</option></Select></Field>
          {form.owner_type === 'CONTRACT' && <Field label="Client"><Select value={form.owner_client_id} onChange={set('owner_client_id')}><option value="">Select...</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>}
        </Grid2>
        <Grid2>
          <Field label="Gross weight (kg)"><Input type="number" min="0" step="0.01" value={form.gross_weight_kg} onChange={set('gross_weight_kg')} /></Field>
        </Grid2>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || (putItem && Number(putQty) > 0 && !putSrc)}>{saving ? 'Creating...' : 'Create Unit'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Move modal ────────────────────────────────────────────────────────────────
function MoveModal({ unitId, onClose, onSaved }) {
  const { data: locData } = useApi('/warehouse/locations');
  const locations = locData?.locations ?? locData ?? [];
  const [form, setForm] = useState({ to_location_id: '', notes: '' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const r = await apiFetch(`/storage-units/${unitId}/move`, { method: 'POST', body: form });
      if (r?.warning) window.setTimeout(() => window.alert(r.warning), 0);
      onSaved();
    }
    catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title="Move Unit" onClose={onClose} width={440}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Field label="New location *"><LocationSelect locations={locations} value={form.to_location_id} onChange={e => setForm(f => ({ ...f, to_location_id: e.target.value }))} required placeholder="Select location..." /></Field>
        <Field label="Notes"><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Moving...' : 'Move'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Weights modal ────────────────────────────────────────────────────────────
// Gross / net weight can be filled in (or corrected) any time — usually once
// the pallet's built and weighed, after it was created.
function WeightsModal({ unit, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: unit.name ?? '', unit_type: unit.unit_type || 'pallet', status: unit.status || 'active',
    quantity: unit.quantity ?? '',
    gross_weight_kg: unit.gross_weight_kg ?? '', net_weight_kg: unit.net_weight_kg ?? '',
  });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch(`/storage-units/${unit.id}`, { method: 'PUT', body: {
        name: form.name,
        unit_type: form.unit_type,
        status: form.status,
        quantity: form.quantity === '' ? null : parseFloat(form.quantity),
        gross_weight_kg: form.gross_weight_kg === '' ? null : parseFloat(form.gross_weight_kg),
        net_weight_kg: form.net_weight_kg === '' ? null : parseFloat(form.net_weight_kg),
      } });
      onSaved();
    } catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title={`Edit ${unit.unit_ref}`} onClose={onClose} width={480}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Field label="Name"><Input value={form.name} onChange={set('name')} placeholder="e.g. Cimbroni October export" autoFocus /></Field>
        <Grid2>
          <Field label="Unit type"><Select value={form.unit_type} onChange={set('unit_type')}>
            {['pallet','ibc','cask','case'].map(t => <option key={t} value={t}>{t}</option>)}
          </Select></Field>
          <Field label="Status"><Select value={form.status} onChange={set('status')}>
            {[['active','Active'],['empty','Empty'],['in_transit','In transit'],['dispatched','Dispatched'],['destroyed','Destroyed']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </Select></Field>
        </Grid2>
        <Field label="Quantity"><Input type="number" min="0" step="0.001" value={form.quantity} onChange={set('quantity')} placeholder="e.g. 1200 (bottles)" /></Field>
        <Grid2>
          <Field label="Gross weight (kg)"><Input type="number" min="0" step="0.01" value={form.gross_weight_kg} onChange={e => setForm(f => ({ ...f, gross_weight_kg: e.target.value }))} placeholder="incl. pallet & packaging" /></Field>
          <Field label="Net weight (kg)"><Input type="number" min="0" step="0.01" value={form.net_weight_kg} onChange={e => setForm(f => ({ ...f, net_weight_kg: e.target.value }))} placeholder="goods only (optional)" /></Field>
        </Grid2>
        <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-muted)' }}>These describe the unit; they don't move stock or duty. Use Move, Change owner and Edit contents for those. Duty status isn't set here: it's that of the stock on the unit, and changes only with a duty release or despatch. The label always shows the current details; a manifest is a fixed snapshot, so generate a new one after editing.</p>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Change owner modal ───────────────────────────────────────────────────────
function ChangeOwnerModal({ unitId, currentOwnerType, currentOwnerClientId, onClose, onSaved }) {
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData ?? [];
  const [ownerType, setOwnerType] = useState(currentOwnerType || 'PLD');
  const [ownerClientId, setOwnerClientId] = useState(currentOwnerClientId || '');
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      if (ownerType === 'CONTRACT' && !ownerClientId) throw new Error('Select a client');
      await apiFetch(`/storage-units/${unitId}`, {
        method: 'PUT',
        body: { owner_type: ownerType, owner_client_id: ownerType === 'CONTRACT' ? ownerClientId : undefined },
      });
      onSaved();
    } catch(e2) { setErr(e2.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Change Owner" onClose={onClose} width={440}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Field label="Owner *">
          <Select value={ownerType} onChange={e => { setOwnerType(e.target.value); if (e.target.value !== 'CONTRACT') setOwnerClientId(''); }}>
            <option value="PLD">PLD</option>
            <option value="CONTRACT">Client</option>
          </Select>
        </Field>
        {ownerType === 'CONTRACT' && (
          <Field label="Client *">
            <Select value={ownerClientId} onChange={e => setOwnerClientId(e.target.value)} required>
              <option value="">Select client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
        )}
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Owner'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Stock item search (mirrors the Stock Take "search existing items" pattern) ─
function ContentLineSearch({ query, hasSelection, onQueryChange, onSelect }) {
  const [results, setResults] = useState([]);
  useEffect(() => {
    // Once a line has a confirmed selection, don't re-search on its own name —
    // otherwise picking a match immediately re-opens the same dropdown.
    if (hasSelection || !query || !query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { stock_items } = await apiFetch(`/stock?search=${encodeURIComponent(query)}`);
        setResults(stock_items || []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [query, hasSelection]);

  return (
    <div style={{ position: 'relative' }}>
      <Input placeholder="Search stock item…" value={query} onChange={e => onQueryChange(e.target.value)} />
      {!hasSelection && results.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginTop: 4, maxHeight: 180, overflowY: 'auto', boxShadow: 'var(--shadow-lg)' }}>
          {results.map(i => (
            <div key={i.id} onClick={() => { onSelect(i); setResults([]); }}
              style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
              {i.name} <span style={{ color: 'var(--text-muted)' }}>({i.sku})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Putting stock on / taking it off a unit (Joe, 15 Oct 2026) ─────────────
// Real stock moves: from loose stock where the unit is, or moved from where
// the stock actually is (e.g. Unallocated). Nothing is just typed in.
const fmtQty = (v) => Number(v).toLocaleString('en-GB', { maximumFractionDigits: 3 });
const DUTY_SHORT = { in_bond: 'in bond', duty_paid: 'duty paid', not_applicable: '' };

/** Where an item is, with how much is loose at each place — pick one to take it from. */
function StockSourcePicker({ itemId, unitLocationId, quantity, value, onChange, unit }) {
  const [sources, setSources] = useState(null);
  useEffect(() => {
    setSources(null);
    if (!itemId) return;
    apiFetch(`/storage-units/stock-sources?item=${itemId}`).then(d => {
      const list = Array.isArray(d) ? d : d?.data ?? [];
      setSources(list);
      if (!value || !list.some(x => `${x.location_id}|${x.duty_status}` === value)) {
        const own = list.find(x => x.location_id === unitLocationId && x.loose > 0);
        const best = own || list.find(x => x.loose >= (Number(quantity) || 0)) || list[0];
        onChange(best ? `${best.location_id}|${best.duty_status}` : '');
      }
    }).catch(() => setSources([]));
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!itemId) return null;
  if (!sources) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Looking for where it is…</p>;
  if (!sources.length) return <p style={{ fontSize: 13, color: '#b45309' }}>Blake has none of this in stock anywhere. Book it in (goods in, production output or a stock adjustment) first.</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {sources.map(src => {
        const key = `${src.location_id}|${src.duty_status}`;
        const on = value === key;
        const short = Number(quantity) > 0 && src.loose < Number(quantity) - 0.0001;
        return (
          <button key={key} type="button" onClick={() => onChange(key)} aria-pressed={on} disabled={src.loose <= 0}
            style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 12px', borderRadius: 8, textAlign: 'left', fontFamily: 'inherit', fontSize: 13,
              cursor: src.loose > 0 ? 'pointer' : 'not-allowed', opacity: src.loose > 0 ? 1 : 0.6,
              border: on ? '2px solid var(--pld-green)' : '1px solid var(--border)', background: on ? '#eef1e8' : 'var(--surface)', color: 'var(--text)' }}>
            <span><strong>{src.label}</strong>{src.location_id === unitLocationId ? ' (where this unit is)' : ''}{DUTY_SHORT[src.duty_status] ? ` · ${DUTY_SHORT[src.duty_status]}` : ''}</span>
            <span style={{ color: short ? '#b45309' : 'var(--text-muted)' }}>
              {fmtQty(src.loose)} {unit || ''} loose{src.units.length ? ` · ${fmtQty(src.on_units)} on ${src.units.map(u => u.unit_ref).join(', ')}` : ''}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function PutOnModal({ unit, onClose, onSaved }) {
  const [item, setItem] = useState(null);
  const [query, setQuery] = useState('');
  const [qty, setQty] = useState('');
  const [src, setSrc] = useState('');
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const [fromId, status] = src.split('|');
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch(`/storage-units/${unit.id}/put`, { method: 'POST', body: { stock_item_id: item.id, quantity: parseFloat(qty), from_location_id: fromId, duty_status: status } });
      onSaved();
    } catch (e2) { setErr(e2.message); } finally { setSaving(false); }
  }
  return (
    <Modal title={`Put stock on ${unit.unit_ref}`} onClose={onClose} width={600}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Field label="What's going on it">
          <ContentLineSearch query={query} hasSelection={!!item} onQueryChange={v => { setQuery(v); setItem(null); }} onSelect={it => { setItem(it); setQuery(it.name); }} />
        </Field>
        <Field label={`How much${item?.unit ? ` (${item.unit})` : ''}`}><Input type="number" min="0" step="any" value={qty} onChange={e => setQty(e.target.value)} /></Field>
        {item && <Field label="Where it is now"><StockSourcePicker itemId={item.id} unitLocationId={unit.location_id} quantity={qty} value={src} onChange={setSrc} unit={item.unit} /></Field>}
        {item && fromId && unit.location_id && fromId !== unit.location_id && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>It's moved from there to where {unit.unit_ref} is{unit.location_code ? ` (${unit.location_code})` : ''}.</p>}
        {item && fromId && !unit.location_id && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>{unit.unit_ref} has no location yet — it's taken to be there.</p>}
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !item || !(Number(qty) > 0) || !fromId}>{saving ? 'Saving…' : 'Put on'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

function TakeOffModal({ unit, line, onClose, onSaved }) {
  const { data: locData } = useApi('/warehouse/locations');
  const locations = locData?.locations ?? locData ?? [];
  const [qty, setQty] = useState(String(line.quantity));
  const [mode, setMode] = useState('loose');
  const [to, setTo] = useState('');
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch(`/storage-units/${unit.id}/take-off`, { method: 'POST', body: { stock_item_id: line.stock_item_id, quantity: parseFloat(qty), to_location_id: mode === 'move' ? to : undefined } });
      onSaved();
    } catch (e2) { setErr(e2.message); } finally { setSaving(false); }
  }
  return (
    <Modal title={`Take ${line.name} off ${unit.unit_ref}`} onClose={onClose} width={520}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <Field label={`How much (${line.unit || ''}) — ${fmtQty(line.quantity)} on it`}><Input type="number" min="0" step="any" value={qty} onChange={e => setQty(e.target.value)} /></Field>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 8, cursor: 'pointer' }}>
          <input type="radio" checked={mode === 'loose'} onChange={() => setMode('loose')} /> It stays here, loose{unit.location_code ? ` (${unit.location_code})` : ''}
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 8, cursor: 'pointer' }}>
          <input type="radio" checked={mode === 'move'} onChange={() => setMode('move')} /> It's moving to…
        </label>
        {mode === 'move' && <LocationSelect locations={locations} value={to} onChange={e => setTo(e.target.value)} placeholder="— Where it's going —" />}
        <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Gone for good (used, broken, sent)? That's a stock adjustment or write-off on the stock item, not this.</p>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !(Number(qty) > 0) || (mode === 'move' && !to)}>{saving ? 'Saving…' : 'Take off'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

/** Units whose contents aren't backed by stock where they are — and the fix. */
function UnitCheckModal({ problems, onClose, onChanged }) {
  const { data: locData } = useApi('/warehouse/locations');
  const locations = locData?.locations ?? locData ?? [];
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(''); const [done, setDone] = useState([]);
  const [placeAt, setPlaceAt] = useState({});
  async function fix(p, body, note) {
    setBusy(true); setErr('');
    try {
      await apiFetch('/storage-units/check/fix', { method: 'POST', body: { location_id: p.location_id, stock_item_id: p.stock_item_id, duty_status: p.duty_status, ...body } });
      setDone(d => [...d, note]); onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <Modal title="Units that don't match the stock" onClose={onClose} width={680}>
      <Err msg={err} />
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0 }}>These units were filled in without the stock being moved onto them, so Blake still has it somewhere else. Bring it to them in one go, or take it off them if it isn't really there.</p>
      {done.map((d, i) => <p key={i} style={{ fontSize: 13, color: '#166534', margin: '0 0 6px' }}>✓ {d}</p>)}
      {problems.length === 0 && <p style={{ fontSize: 14 }}>Everything matches now.</p>}
      {problems.map(p => {
        const units = p.units.map(u => `${u.unit_ref}${u.name ? ` (${u.name})` : ''}`).join(', ');
        const key = `${p.location_id}|${p.stock_item_id}|${p.duty_status}`;
        return (
          <div key={key} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{p.item_name}</div>
            {p.kind === 'no_location' ? (<>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 8px' }}>{units} hold {fmtQty(p.on_units)} {p.unit} but have no location. Where are they?</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 260px' }}><LocationSelect locations={locations} value={placeAt[key] || ''} onChange={e => setPlaceAt(x => ({ ...x, [key]: e.target.value }))} placeholder="— Where they are —" /></div>
                <Btn size="sm" disabled={busy || !placeAt[key]} onClick={() => fix({ ...p, location_id: placeAt[key] }, { action: 'place', location_id: placeAt[key], storage_unit_ids: p.units.map(u => u.storage_unit_id) }, `${units} placed`)}>They're here</Btn>
              </div>
            </>) : (<>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 8px' }}>
                {units} at {p.location} hold {fmtQty(p.on_units)} {p.unit}{DUTY_SHORT[p.duty_status] ? ` ${DUTY_SHORT[p.duty_status]}` : ''}, but Blake has {fmtQty(p.held)} {p.unit}{DUTY_SHORT[p.duty_status] ? ` ${DUTY_SHORT[p.duty_status]}` : ''} there — {fmtQty(p.short)} {p.unit} short.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {p.sources.map(src => (
                  <Btn key={`${src.location_id}|${src.duty_status}`} size="sm" disabled={busy}
                    onClick={() => fix(p, { action: 'move', from_location_id: src.location_id }, `${fmtQty(Math.min(p.short, src.loose))} ${p.unit} ${p.item_name} moved from ${src.label} to ${p.location}`)}>
                    Move {fmtQty(Math.min(p.short, src.loose))} {p.unit} here from {src.label}
                  </Btn>
                ))}
                <Btn size="sm" variant="ghost" disabled={busy} onClick={() => fix(p, { action: 'trim' }, `${fmtQty(p.short)} ${p.unit} ${p.item_name} taken off ${units}`)}>It isn't there — take it off the units</Btn>
              </div>
              {!p.sources.length && <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>Blake has none of it loose anywhere else. If it really is on the units, book it in with a stock adjustment first.</div>}
            </>)}
          </div>
        );
      })}
      <BtnRow><Btn variant="ghost" onClick={onClose}>Close</Btn></BtnRow>
    </Modal>
  );
}

// ── Manifest PDF viewer helper ───────────────────────────────────────────────
// apiFetch always does res.json(), so a raw fetch (with the same bearer token)
// is used here to pull back a PDF blob and open it in a new tab.
async function viewManifestPdf(unitId, manifestId) {
  const token = localStorage.getItem('pld_token');
  const res = await fetch(`/api/storage-units/${unitId}/manifest/${manifestId}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load manifest PDF');
  const blob = await res.blob();

  // A plain blob: URL carries no filename, so Chrome's PDF viewer falls back
  // to "document" when you hit Save. Wrapping the blob in a File — using the
  // filename the server sent via Content-Disposition — carries a real name
  // through to the save dialog.
  const disposition = res.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match ? match[1] : `manifest-${manifestId}.pdf`;
  const file = new File([blob], filename, { type: 'application/pdf' });

  const url = URL.createObjectURL(file);
  window.open(url, '_blank');
}

// Same File-wrap pattern as viewManifestPdf, for the printable pallet label.
async function viewPalletLabelPdf(unitId, unitRef) {
  const token = localStorage.getItem('pld_token');
  const res = await fetch(`/api/storage-units/${unitId}/label.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load pallet label PDF');
  const blob = await res.blob();

  const disposition = res.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match ? match[1] : `${unitRef || 'pallet'}-label.pdf`;
  const file = new File([blob], filename, { type: 'application/pdf' });

  const url = URL.createObjectURL(file);
  window.open(url, '_blank');
}

// ── Unit detail ───────────────────────────────────────────────────────────────
// ── Clone (Joe, 27 Oct 2026) ──────────────────────────────────────────────────
// More units like this one; with its contents, the stock comes off loose stock.
function CloneModal({ unit, contents, onClose, onSaved }) {
  const { data: locData } = useApi('/warehouse/locations');
  const locations = locData?.locations ?? locData ?? [];
  const [copies, setCopies] = useState('1');
  const [loc, setLoc] = useState(unit.location_id || '');
  const [withContents, setWithContents] = useState(contents.length > 0);
  const [from, setFrom] = useState('');
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const n = Math.max(1, Math.floor(Number(copies) || 1));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const r = await apiFetch(`/storage-units/${unit.id}/clone`, { method: 'POST', body: { copies: n, location_id: loc || null, with_contents: withContents, from_location_id: from || loc || null } });
      const made = (r.data ?? r).made || [];
      onSaved(made);
    } catch (e2) { setErr(e2.message); } finally { setSaving(false); }
  }
  return (
    <Modal title={`Clone ${unit.unit_ref}`} onClose={onClose} width={480}>
      <form onSubmit={submit}>
        <Err msg={err} />
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>New {unit.unit_type}s like this one: same name, owner and weights{withContents ? ', and the same contents' : ''}. Each gets its own reference and label.</p>
        <Grid2>
          <Field label="How many copies"><Input type="number" min="1" max="50" value={copies} onChange={e => setCopies(e.target.value)} /></Field>
          <Field label="Location"><LocationSelect locations={locations} value={loc} onChange={e => setLoc(e.target.value)} placeholder="Same as this one" /></Field>
        </Grid2>
        {contents.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={withContents} onChange={e => setWithContents(e.target.checked)} style={{ marginTop: 3 }} />
              <span>Same contents on each: {contents.map(c => `${fmtQty(c.quantity)} × ${c.name || 'item'}`).join(', ')}
                <span style={{ display: 'block', color: 'var(--text-muted)' }}>This moves real stock onto the copies{n > 1 ? ` (${n} lots)` : ''} from loose stock — nothing is made if there isn't enough.</span></span>
            </label>
            {withContents && (
              <div style={{ marginTop: 10 }}>
                <Field label="Take the stock from"><LocationSelect locations={locations} value={from} onChange={e => setFrom(e.target.value)} placeholder="Where the copies go" /></Field>
              </div>
            )}
          </div>
        )}
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Cloning…' : `Make ${n} cop${n === 1 ? 'y' : 'ies'}`}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

function UnitDetail({ unitId, onBack, onRefresh, onOpenConsignment }) {
  const { data: conData } = useApi(`/consignments?storage_unit_id=${unitId}`, [unitId]);
  const consignments = conData?.data ?? [];
  async function deleteUnit() {
    if (!(await uiConfirm(`Delete ${unit.unit_ref}? The stock on it stays where it is in Blake — only the pallet record goes. A pallet with history is kept on record but taken off the lists.`))) return;
    try {
      const r = await apiFetch(`/storage-units/${unitId}`, { method: 'DELETE' });
      alert(r.removed ? `${r.unit_ref} deleted.` : `${r.unit_ref} taken out of use — it has history (moves, manifests or consignments), so it's kept on record.`);
      onRefresh(); onBack();
    } catch (e) { alert(e.message); }
  }
  const { data, loading, refetch } = useApi(`/storage-units/${unitId}`, [unitId]);
  const unit = data?.storage_unit ?? null;
  const contents = data?.contents ?? [];
  const movements = data?.movements ?? [];
  const { data: manifestsData, refetch: refetchManifests } = useApi(`/storage-units/${unitId}/manifests`, [unitId]);
  const manifests = manifestsData?.manifests ?? [];
  const [modal, setModal] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [manifestErr, setManifestErr] = useState('');

  async function generateManifest() {
    setGenerating(true); setManifestErr('');
    try {
      await apiFetch(`/storage-units/${unitId}/manifest`, { method: 'POST', body: {} });
      refetch();
      refetchManifests();
    } catch(e) { setManifestErr(e.message); } finally { setGenerating(false); }
  }

  async function openManifest(manifestId) {
    try { await viewManifestPdf(unitId, manifestId); }
    catch(e) { alert(e.message); }
  }

  async function printLabel() {
    try { await viewPalletLabelPdf(unitId, unit.unit_ref); }
    catch(e) { alert(e.message); }
  }

  if (loading || !unit) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div>
      {modal === 'move'     && <MoveModal     unitId={unitId} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); onRefresh(); }} />}
      {modal === 'clone'    && <CloneModal unit={unit} contents={contents} onClose={() => setModal(null)} onSaved={async (made) => { setModal(null); refetch(); onRefresh(); await uiAlert(`Made ${made.join(', ')}.`); }} />}
      {modal === 'put' && <PutOnModal unit={unit} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); onRefresh(); }} />}
      {modal?.takeOff && <TakeOffModal unit={unit} line={modal.takeOff} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); onRefresh(); }} />}
      {modal === 'weights'  && <WeightsModal unit={unit} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); onRefresh(); }} />}
      {modal === 'owner'    && <ChangeOwnerModal unitId={unitId} currentOwnerType={unit.owner_type} currentOwnerClientId={unit.owner_client_id} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); onRefresh(); }} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0, marginTop: 4 }}>&#8592;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'monospace' }}>{unit.unit_ref}</h2>
            {unit.name && <span style={{ fontSize: 18, fontWeight: 600 }}>{unit.name}</span>}
            <TypeBadge type={unit.unit_type} />
            <DutyBadge status={unit.duty_status} />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {unit.zone_name ? `${unit.zone_name} — ${unit.location_code}` : 'No location'} · Owner: {unit.owner_type === 'CONTRACT' ? (unit.owner_client_name || 'Client') : 'PLD'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Btn size="sm" variant="ghost" onClick={() => setModal('move')}>Move</Btn>
          <Btn size="sm" variant="ghost" onClick={() => setModal('clone')}>Clone</Btn>
          <Btn size="sm" onClick={() => setModal('weights')}>Edit details</Btn>
          <Btn size="sm" variant="ghost" onClick={() => setModal('owner')}>Change owner</Btn>
          <Btn size="sm" variant="ghost" onClick={() => setModal('put')}>+ Put stock on</Btn>
          <Btn size="sm" variant="ghost" onClick={generateManifest} disabled={generating}>{generating ? 'Generating...' : 'Generate manifest'}</Btn>
          <Btn size="sm" variant="ghost" onClick={printLabel}>Preview label</Btn>
          <PrintButton docType="pallet_label" path={`/storage-units/${unitId}/label.pdf`} title={`Pallet label ${unit?.unit_ref || ''}`} label="Print label" />
          <Btn size="sm" variant="danger" onClick={deleteUnit}>Delete</Btn>
        </div>
      </div>
      {consignments.filter(c => c.status !== 'cancelled').length > 0 && (
        <p style={{ fontSize: 13, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '9px 14px', marginBottom: 16 }}>
          On {consignments.filter(c => c.status !== 'cancelled').map((c, i) => (
            <span key={c.id}>{i ? ', ' : ''}<a href="#" onClick={e => { e.preventDefault(); onOpenConsignment?.(c.id); }} style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--pld-green)' }}>{c.consignment_ref}</a> ({c.status}{c.order_label ? ` — ${c.order_label}` : ''})</span>
          ))}
        </p>
      )}

      {manifestErr && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{manifestErr}</p>}
      {unit.duty_status === 'mixed' && (
        <p style={{ background: '#fef3c7', color: '#92400e', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
          This unit holds both bonded and duty-paid stock. Bonded and duty-paid goods should be kept on separate pallets — split them when you can. It's charged at the bonded storage rate meanwhile.
        </p>
      )}

      {/* Info grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Quantity', value: unit.quantity ? `${unit.quantity} ${unit.unit || ''}`.trim() : '—' },
          { label: 'Gross weight', value: unit.gross_weight_kg ? `${unit.gross_weight_kg}kg` : '—' },
          { label: 'Net weight', value: unit.net_weight_kg ? `${unit.net_weight_kg}kg` : '—' },
          { label: 'Status', value: (unit.status || '—').replace(/_/g, ' ') },
          { label: 'Created', value: fmt(unit.created_at) },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Contents */}
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Contents</h3>
      {contents.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>Empty. Use "+ Put stock on" — it comes from where the stock is now.</p> : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 24 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--surface-2)' }}>{['Item','SKU','Category','Qty','Unit','Duty',''].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>)}</tr></thead>
            <tbody>
              {contents.map(c => (
                <tr key={c.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 14px', fontWeight: 600 }}><a href={`/stock?item=${c.stock_item_id}`} style={{ color: 'var(--text)' }}>{c.name}</a></td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{c.sku}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{c.category}</td>
                  <td style={{ padding: '9px 14px' }}>{c.quantity}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{c.unit}</td>
                  <td style={{ padding: '9px 14px' }}>{c.is_alcoholic ? <DutyBadge status={c.duty_status} /> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td style={{ padding: '9px 14px', textAlign: 'right' }}><Btn size="sm" variant="ghost" onClick={() => setModal({ takeOff: c })}>Take off</Btn></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Movement history */}
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Movement history</h3>
      {movements.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>No movements recorded.</p> : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 24 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--surface-2)' }}>{['Date','From','To','Notes'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>)}</tr></thead>
            <tbody>
              {movements.map(m => (
                <tr key={m.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 14px' }}>{fmt(m.created_at)}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{m.from_location_code || '—'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: 12 }}>{m.to_location_code || '—'}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{m.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manifest history */}
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Manifest history</h3>
      {manifests.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No manifests generated yet. Use "Generate manifest" above to snapshot this unit's current contents, weights and location into a printable document.</p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--surface-2)' }}>{['Ref','Generated','Signed off','',].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>)}</tr></thead>
            <tbody>
              {manifests.map(m => (
                <tr key={m.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontWeight: 600 }}>{m.manifest_ref}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{fmt(m.created_at)}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{m.signed_off_by_name ? `${m.signed_off_by_name} · ${fmt(m.signed_off_at)}` : 'Not yet signed off'}</td>
                  <td style={{ padding: '9px 14px', textAlign: 'right' }}><span style={{ display: 'inline-flex', gap: 8, justifyContent: 'flex-end' }}><Btn size="sm" variant="ghost" onClick={() => openManifest(m.id)}>View</Btn><PrintButton docType="pallet_manifest" path={`/storage-units/${unitId}/manifest/${m.id}/pdf`} title="Pallet manifest" /></span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const UNIT_TYPES = ['pallet', 'ibc', 'cask', 'case'];

export default function StorageUnits() {
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(() => new URLSearchParams(window.location.search).get('unit'));
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState('units'); // units | consignments
  const [consignmentId, setConsignmentId] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [addTo, setAddTo] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');
  const togglePick = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const qs = typeFilter !== 'all' ? `?unit_type=${typeFilter}` : '';
  const { data, loading, refetch } = useApi(`/storage-units${qs}`, [typeFilter]);
  const allUnits = data?.storage_units ?? [];
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const filtered = q ? allUnits.filter(u => [u.unit_ref, u.name, u.location_code, u.zone_name, u.owner_client_name].some(v => String(v || '').toLowerCase().includes(q))) : allUnits;
  // Click a column heading to sort by it (again to reverse). Blanks go last.
  const [sort, setSort] = useState({ key: 'Ref', dir: 1 });
  const SORT_VALUE = {
    Ref: u => u.unit_ref, Name: u => u.name, Type: u => u.unit_type, Location: u => u.location_code, Zone: u => u.zone_name,
    Owner: u => (u.owner_type === 'CONTRACT' ? (u.owner_client_name || 'Client') : 'PLD'), 'Duty status': u => u.duty_status,
    Qty: u => (u.quantity == null ? null : Number(u.quantity)), 'Weight (kg)': u => (u.gross_weight_kg == null ? null : Number(u.gross_weight_kg)), Created: u => u.created_at,
  };
  const sortCol = Object.keys(SORT_VALUE).find(k => k === sort.key) || 'Ref';
  const units = [...filtered].sort((a, b) => {
    const va = SORT_VALUE[sortCol](a); const vb = SORT_VALUE[sortCol](b);
    const ea = va == null || va === ''; const eb = vb == null || vb === '';
    if (ea || eb) return ea === eb ? 0 : ea ? 1 : -1;
    const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return c * sort.dir;
  });
  const sortBy = (h) => setSort(s => (s.key === h ? { key: h, dir: -s.dir } : { key: h, dir: 1 }));

  const pickedIds = [...picked].filter(id => allUnits.some(u => u.id === id));
  const { data: checkData, refetch: refetchCheck } = useApi('/storage-units/check');
  const problems = Array.isArray(checkData) ? checkData : checkData?.data ?? [];
  const [showCheck, setShowCheck] = useState(false);
  async function bulkDelete() {
    if (!(await uiConfirm(`Delete ${pickedIds.length} pallet${pickedIds.length === 1 ? '' : 's'}? The stock on them stays where it is in Blake — only the pallet records go. Any with history are kept on record but taken off the lists.`))) return;
    let removed = 0; let retired = 0; const errs = [];
    for (const id of pickedIds) {
      try { const r = await apiFetch(`/storage-units/${id}`, { method: 'DELETE' }); r.removed ? removed++ : retired++; }
      catch (e) { errs.push(e.message); }
    }
    setPicked(new Set()); refetch();
    setBulkMsg(`${removed ? `${removed} deleted. ` : ''}${retired ? `${retired} taken out of use (kept on record — they have history). ` : ''}${errs.length ? `Problems: ${errs.join('; ')}` : ''}`);
  }

  if (selectedId) return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 8px' }}>
      <UnitDetail unitId={selectedId} onBack={() => setSelectedId(null)} onRefresh={refetch}
        onOpenConsignment={(id) => { setSelectedId(null); setTab('consignments'); setConsignmentId(id); }} />
    </div>
  );
  if (tab === 'consignments' && consignmentId) return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 8px' }}>
      <ConsignmentDetail id={consignmentId} onBack={() => setConsignmentId(null)} onOpenPallet={(id) => setSelectedId(id)} />
    </div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      {showCreate && <CreateUnitModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Storage Units</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Track pallets, IBCs, cases and casks — contents, location and movement history</p>
        </div>
        {tab === 'units' && <Btn onClick={() => setShowCreate(true)}>+ New Unit</Btn>}
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 18 }}>
        {[['units', 'Units'], ['consignments', 'Consignments']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none', borderBottom: `2px solid ${tab === k ? 'var(--pld-green)' : 'transparent'}`, color: tab === k ? 'var(--text)' : 'var(--text-muted)', marginBottom: -1 }}>{l}</button>
        ))}
      </div>

      {tab === 'consignments' ? <ConsignmentList onOpen={setConsignmentId} /> : (<>
      {addTo && <AddToConsignmentModal palletIds={pickedIds} onClose={() => setAddTo(false)} onDone={(id) => { setAddTo(false); setPicked(new Set()); setTab('consignments'); setConsignmentId(id || null); }} />}

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, ref, location…"
          style={{ padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)', width: 220, marginRight: 8 }} />
        {['all', ...UNIT_TYPES].map(t => (
          <button key={t} onClick={() => setTypeFilter(t)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: typeFilter === t ? 'var(--pld-green)' : 'var(--surface-2)', color: typeFilter === t ? '#fff' : 'var(--text-muted)' }}>{t}</button>
        ))}
      </div>

      {pickedIds.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '9px 12px', marginBottom: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13 }}>
          <strong>{pickedIds.length} selected</strong>
          <Btn size="sm" variant="ghost" onClick={() => openPdf(`/storage-units/labels.pdf?ids=${pickedIds.join(',')}`)}>Preview labels</Btn>
          <PrintButton docType="pallet_labels" path={`/storage-units/labels.pdf?ids=${pickedIds.join(',')}`} title={`${pickedIds.length} pallet labels`} label={`Print ${pickedIds.length} label${pickedIds.length === 1 ? '' : 's'}`} />
          <Btn size="sm" onClick={() => setAddTo(true)}>Add to consignment…</Btn>
          <Btn size="sm" variant="danger" onClick={bulkDelete}>Delete</Btn>
          <span style={{ flex: 1 }} />
          <Btn size="sm" variant="ghost" onClick={() => setPicked(new Set())}>Clear</Btn>
        </div>
      )}
      {bulkMsg && <p style={{ fontSize: 13, color: 'var(--pld-green)', margin: '0 0 12px' }}>{bulkMsg}</p>}
      {problems.length > 0 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
          <span><strong>{problems.reduce((t, p) => t + p.units.length, 0)} unit{problems.reduce((t, p) => t + p.units.length, 0) === 1 ? '' : 's'}</strong> hold stock that Blake still has somewhere else ({[...new Set(problems.map(p => p.item_name))].join(', ')}).</span>
          <Btn size="sm" onClick={() => setShowCheck(true)}>Sort it out</Btn>
        </div>
      )}
      {showCheck && <UnitCheckModal problems={problems} onClose={() => setShowCheck(false)} onChanged={() => { refetchCheck(); refetch(); }} />}

      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : units.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
          <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>No storage units found</p>
          <p style={{ fontSize: 13, margin: 0 }}>Create your first storage unit above.</p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                <th style={{ padding: '9px 0 9px 14px', width: 18 }}>
                  <input type="checkbox" title="Select all shown" checked={units.length > 0 && units.every(u => picked.has(u.id))}
                    onChange={e => setPicked(e.target.checked ? new Set(units.map(u => u.id)) : new Set())} />
                </th>
                {['Ref', 'Name', 'Type', 'Location', 'Zone', 'Owner', 'Duty status', 'Qty', 'Weight (kg)', 'Created'].map(h => (
                  <th key={h} onClick={() => sortBy(h)} title={`Sort by ${h.toLowerCase()}`} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: sortCol === h ? 'var(--text)' : 'var(--text-muted)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>{h}{sortCol === h ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {units.map(u => (
                <tr key={u.id} onClick={() => setSelectedId(u.id)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '11px 0 11px 14px' }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={picked.has(u.id)} onChange={() => togglePick(u.id)} />
                  </td>
                  <td style={{ padding: '11px 14px', fontWeight: 700, fontFamily: 'monospace' }}>{u.unit_ref}{u.consignment_ref && <div style={{ fontSize: 10.5, fontWeight: 600, color: '#92400e' }} title="On this open consignment">{u.consignment_ref}</div>}</td>
                  <td style={{ padding: '11px 14px', fontWeight: 600 }}>{u.name || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>—</span>}</td>
                  <td style={{ padding: '11px 14px' }}><TypeBadge type={u.unit_type} /></td>
                  <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{u.location_code || '—'}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{u.zone_name || '—'}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{u.owner_type === 'CONTRACT' ? (u.owner_client_name || 'Client') : 'PLD'}</td>
                  <td style={{ padding: '11px 14px' }}><DutyBadge status={u.duty_status} /></td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{u.quantity ?? '—'}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{u.gross_weight_kg ?? '—'}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{fmt(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}
